import React, { FC, useState } from 'react';
import { I18n } from '@fiverr-private/i18n-react';
import { Drawer, DrawerCloseButton, DrawerContent, DrawerHeader } from '@fiverr-private/overlay';
import { theme } from '@fiverr-private/theme';
import { useWorkExperience } from '../../../../hooks/useWorkExperience';
import WorkExperienceForm from '../WorkExperienceForm';
import { T_KEY } from '../../utils';
import { CurrentViewState, WorkExperienceViewStates } from '../../shared/types';
import ImportWorkExperience from '../ImportWorkExperience';
import WorkExperienceListContainer from './WorkExperienceListContainer';
import SaveChangesModal from './SaveChangesModal';

interface WorkExperienceDrawerProps {
    isDrawerOpen: boolean;
    setIsDrawerOpen: (isDrawerOpen: boolean) => void;
    pushkinClient?;
}

const T_DRAWER_KEY = `${T_KEY}.drawer`;

const WorkExperienceDrawer: FC<WorkExperienceDrawerProps> = ({ isDrawerOpen, setIsDrawerOpen, pushkinClient }) => {
    const { hasWorkExperience, workExperiences } = useWorkExperience();
    const [currentWorkExperiences, setCurrentWorkExperiences] = useState(workExperiences);
    const [initialWorkExperiences, setInitialWorkExperiences] = useState(currentWorkExperiences);

    const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);

    const [currentViewState, setCurrentViewState] = useState<CurrentViewState>({
        viewState: hasWorkExperience ? WorkExperienceViewStates.List : WorkExperienceViewStates.Empty,
    });
    const { isDirty, viewState, experienceId: currentExperienceId } = currentViewState;

    const currentExperience = currentWorkExperiences?.find((experience) => experience.id === currentExperienceId);

    const closeDrawer = () => {
        setIsSaveChangesModalOpen(false);
        setIsDrawerOpen(false);
        setCurrentWorkExperiences(initialWorkExperiences);
    };

    const onAnimationEndHandler = () => {
        if (isDrawerOpen) {
            return;
        }

        const workExperienceViewState = hasWorkExperience
            ? WorkExperienceViewStates.List
            : WorkExperienceViewStates.Empty;
        setCurrentViewState({ viewState: workExperienceViewState, isDirty: false });
    };

    const handleClose = () => {
        if (isDirty) {
            setIsSaveChangesModalOpen(true);
        } else {
            closeDrawer();
        }
    };

    const importProps = {
        setCurrentViewState,
        setCurrentWorkExperiences,
        pushkinClient,
    };

    const listProps = {
        currentViewState,
        setCurrentViewState,
        currentWorkExperiences,
        setIsDrawerOpen,
        initialWorkExperiences,
        setInitialWorkExperiences,
        hasWorkExperience,
    };

    const formProps = {
        currentViewState,
        setCurrentViewState,
        currentExperience,
        currentWorkExperiences,
        setCurrentWorkExperiences,
    };

    const isSm = window?.innerWidth < theme.numericBreakpoints.md;
    const drawerSize = isSm ? 'md' : 'lg';

    const getDrawerTitle = () => {
        if (viewState === WorkExperienceViewStates.AddEdit) {
            return currentExperienceId ? 'edit' : 'add';
        }

        return 'default';
    };

    const shouldShowExperienceList = viewState === WorkExperienceViewStates.List;
    const shouldShowExperienceForm = viewState === WorkExperienceViewStates.AddEdit;
    const shouldShowImportState = viewState === WorkExperienceViewStates.Empty;

    return (
        <>
            <Drawer onClose={handleClose} isOpen={isDrawerOpen}>
                <DrawerContent
                    size={drawerSize}
                    isFullScreen={isSm}
                    position={isSm ? 'bottom' : 'right'}
                    data-testId="work-experience-drawer"
                    onAnimationEnd={onAnimationEndHandler}
                >
                    <DrawerHeader>
                        <I18n k={`${T_DRAWER_KEY}.title.${getDrawerTitle()}`} />
                        <DrawerCloseButton onClick={handleClose} />
                    </DrawerHeader>
                    {shouldShowImportState && <ImportWorkExperience {...importProps} />}
                    {shouldShowExperienceList && <WorkExperienceListContainer {...listProps} />}
                    {shouldShowExperienceForm && <WorkExperienceForm {...formProps} />}
                </DrawerContent>
            </Drawer>
            <SaveChangesModal
                isSaveChangesModalOpen={isSaveChangesModalOpen}
                setIsSaveChangesModalOpen={setIsSaveChangesModalOpen}
                closeDrawer={closeDrawer}
            />
        </>
    );
};

export default WorkExperienceDrawer;
